"use client";

import { useMemoryMonitor } from "@/hooks/useMemoryMonitor";
import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@workspace/ui/components/card";
import { Activity, Download, Trash2, XCircle } from "lucide-react";
import { useState } from "react";

/**
 * Development widget to monitor memory usage
 * Only renders in development mode
 */
export function MemoryMonitorWidget() {
  const { stats, isMonitoring, exportSnapshots, clear } = useMemoryMonitor();
  const [isMinimized, setIsMinimized] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  // Only show in development
  if (process.env.NODE_ENV !== "development" || !isVisible) {
    return null;
  }

  const handleExport = () => {
    const data = exportSnapshots();
    const blob = new Blob([data], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `memory-snapshots-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatBytes = (bytes: number) => {
    const mb = bytes / 1024 / 1024;
    return `${mb.toFixed(2)} MB`;
  };

  if (isMinimized) {
    return (
      <div className="fixed right-4 bottom-4 z-50">
        <Button
          onClick={() => setIsMinimized(false)}
          variant="outline"
          size="sm"
          className="bg-white shadow-lg"
        >
          <Activity className="mr-2 h-4 w-4" />
          Memory Monitor
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed right-4 bottom-4 z-50 w-80">
      <Card className="bg-white/30 shadow-2xl backdrop-blur-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Activity className="h-4 w-4" />
              Memory Monitor
            </CardTitle>
            <div className="flex gap-1">
              <Button
                onClick={() => setIsMinimized(true)}
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
              >
                −
              </Button>
              <Button
                onClick={() => setIsVisible(false)}
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
              >
                <XCircle className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Status */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-600">Status</span>
            <Badge variant={isMonitoring ? "default" : "secondary"}>
              {isMonitoring ? "Monitoring" : "Stopped"}
            </Badge>
          </div>

          {stats && (
            <>
              {/* Current Usage */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">Current Usage</span>
                  <span className="font-mono text-sm font-semibold">
                    {formatBytes(stats.current.usedJSHeapSize)}
                  </span>
                </div>

                {/* Progress bar */}
                <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                  <div
                    className={`h-full transition-all ${
                      stats.current.percentage > 80
                        ? "bg-red-500"
                        : stats.current.percentage > 60
                          ? "bg-yellow-500"
                          : "bg-green-500"
                    }`}
                    style={{ width: `${stats.current.percentage}%` }}
                  />
                </div>

                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{stats.current.percentage.toFixed(1)}% of limit</span>
                  <span>{formatBytes(stats.current.jsHeapSizeLimit)}</span>
                </div>
              </div>

              {/* Growth Rate */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">Growth Rate</span>
                <span
                  className={`font-mono text-sm font-semibold ${
                    stats.leakDetected
                      ? "text-red-600"
                      : stats.growthRate > 10
                        ? "text-yellow-600"
                        : "text-green-600"
                  }`}
                >
                  {stats.growthRate > 0 ? "+" : ""}
                  {stats.growthRate.toFixed(2)} MB/min
                </span>
              </div>

              {/* Leak Warning */}
              {stats.leakDetected && (
                <div className="rounded-md bg-red-50 p-2">
                  <p className="text-xs font-medium text-red-800">
                    ⚠️ Potential memory leak detected
                  </p>
                  <p className="mt-1 text-xs text-red-600">
                    Memory growing at {stats.growthRate.toFixed(2)} MB/min
                  </p>
                </div>
              )}

              {/* Snapshots Count */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">Snapshots</span>
                <span className="font-mono text-sm">
                  {stats.snapshots.length}
                </span>
              </div>
            </>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleExport}
              variant="outline"
              size="sm"
              className="flex-1"
              disabled={!stats}
            >
              <Download className="mr-1 h-3 w-3" />
              Export
            </Button>
            <Button
              onClick={clear}
              variant="outline"
              size="sm"
              className="flex-1"
              disabled={!stats}
            >
              <Trash2 className="mr-1 h-3 w-3" />
              Clear
            </Button>
          </div>

          {/* Instructions */}
          <div className="rounded-md bg-gray-50 p-2">
            <p className="text-xs text-gray-600">
              💡 Open Chrome DevTools → Memory to profile heap snapshots
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
